import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { PasswordChecklist, TextField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';
import { mutationErrorMessage, mutationErrorTone } from '@/lib/api/mutation-toast';

import { CHANGE_PASSWORD_COPY, PROFILE_ERROR_CODES } from '../../constants';
import { useChangePassword } from '../../hooks';
import {
  CHANGE_PASSWORD_MAX_LENGTH,
  CHANGE_PASSWORD_RULES,
  isChangePasswordValid,
} from '../../password-rules';

const schema = z
  .object({ newPassword: z.string(), confirmPassword: z.string() })
  .refine((v) => v.confirmPassword === v.newPassword, {
    path: ['confirmPassword'],
    message: CHANGE_PASSWORD_COPY.MISMATCH,
  });

export type NewPasswordStepProps = {
  onClose: () => void;
  onDone: () => void;
  /** The verification lapsed between Step 2 and Step 3 (PUT 403 `OTP_VERIFICATION_REQUIRED`). */
  onExpired: () => void;
};

/** Change Password Step 3 (US-117548 §D–F): the new password, its confirmation and the checklist. */
export function NewPasswordStep({ onClose, onDone, onExpired }: NewPasswordStepProps) {
  const toast = useToast();
  const change = useChangePassword();
  const { control, handleSubmit, setError, trigger, formState } = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const [newPassword, confirmPassword] = useWatch({
    control,
    name: ['newPassword', 'confirmPassword'],
  });
  const confirmTouched = Boolean(formState.touchedFields.confirmPassword);

  // Editing New Password after Confirm was touched re-checks the match under Confirm.
  useEffect(() => {
    if (confirmTouched) void trigger('confirmPassword');
  }, [newPassword, confirmTouched, trigger]);

  const canSubmit =
    isChangePasswordValid(newPassword) &&
    newPassword === confirmPassword &&
    !formState.errors.newPassword;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await change.mutateAsync(values.newPassword);
      onDone();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === PROFILE_ERROR_CODES.PASSWORD_MUST_BE_DIFFERENT
      ) {
        // Cleared on the next edit: the field re-validates and the resolver passes it.
        setError('newPassword', { type: 'server', message: CHANGE_PASSWORD_COPY.MUST_DIFFER });
        return;
      }
      if (
        error instanceof ApiError &&
        error.code === PROFILE_ERROR_CODES.OTP_VERIFICATION_REQUIRED
      ) {
        onExpired();
        return;
      }
      toast[mutationErrorTone(error)]({ title: mutationErrorMessage(error) }); // values kept
    }
  });

  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={CHANGE_PASSWORD_COPY.STEP3_TITLE}
      subtitle={CHANGE_PASSWORD_COPY.STEP3_SUBTITLE}
      primaryText="Change Password"
      onPrimary={() => void onSubmit()}
      primaryDisabled={!canSubmit}
      loading={formState.isSubmitting}
    >
      <TextField
        name="newPassword"
        control={control}
        label="New Password"
        type="password"
        maxLength={CHANGE_PASSWORD_MAX_LENGTH}
        required
        autoFocus
      />
      <TextField
        name="confirmPassword"
        control={control}
        label="Confirm Password"
        type="password"
        required
      />
      <PasswordChecklist value={newPassword} rules={CHANGE_PASSWORD_RULES} />
    </Modal>
  );
}
